import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import LockedCard from '../../components/gating/LockedCard';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import ResultCard from '../search/components/ResultCard';
import { listSpecialDeals } from '../../services/specialDealsService';
import type { Property } from '../../mocks/properties';

export default function SpecialDealsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { session } = useAuth();
  const { isSubscribed, loading: subLoading } = useSubscription();
  const isRTL = i18n.language === 'ar';
  const token = session?.token ?? null;

  const [deals, setDeals] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !isSubscribed) return;
    setLoading(true);
    setError(null);
    listSpecialDeals(token)
      .then(setDeals)
      .catch(() => setError(t('special.loadError', { defaultValue: 'Could not load special deals.' })))
      .finally(() => setLoading(false));
  }, [token, isSubscribed]);

  // The shared «locked» card for guests + non-subscribers. Gold: Special Deals is
  // the subscriber benefit (THEME.md §2.4c); the lock sits in the title line.
  const locked = (signedIn: boolean) => (
    <LockedCard
      tone="gold"
      className="mt-6"
      title={t('special.lockedTitle', { defaultValue: 'A subscriber benefit' })}
      body={
        signedIn
          ? t('special.lockedSub', { defaultValue: 'Special deals are exclusive to Safaqat24 subscribers. Subscribe to unlock them.' })
          : t('special.lockedGuest', { defaultValue: 'Sign in and subscribe to unlock exclusive Safaqat24 special deals.' })
      }
      primary={{
        label: signedIn ? t('special.subscribe', { defaultValue: 'View subscriptions' }) : t('common.signIn', { defaultValue: 'Sign in' }),
        to: signedIn ? '/membership' : '/signin',
      }}
    />
  );

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
      <Navigation />
      <div className="max-w-6xl mx-auto py-8 px-4">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-gray-100 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#B58A0A]" /> {t('special.title', { defaultValue: 'Special deals' })}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('special.subtitle', { defaultValue: 'Exclusive featured listings, available to Safaqat24 subscribers.' })}
          </p>
        </header>

        {error ? (
          <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
        ) : null}

        {subLoading ? (
          <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        ) : !token ? (
          locked(false)
        ) : !isSubscribed ? (
          locked(true)
        ) : loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-10 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center text-gray-500 py-12">{t('special.empty', { defaultValue: 'No special deals available right now. Check back soon.' })}</div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {deals.map((p, i) => (
              <ResultCard key={p.id} property={p} eagerImage={i < 3} onSelect={(item) => navigate(`/property/${item.id}`)} />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
